import { useState } from "react";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api, qs } from "@/lib/api";
import type { Contact, MatchReview } from "@/lib/types";
import { formatCents, formatDate, formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { BrandMark } from "@/components/BrandMark";
import { Empty, PageHeader } from "@/components/PageHeader";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

type Status = "pending" | "merged" | "dismissed";

const ruleLabel: Record<string, string> = {
  phone_last_name: "Same phone and last name",
  name_address: "Same name and address",
  manual: "Flagged by someone",
};

/** The duplicate review queue: pairs that might be one person. Email matches merge on their own;
 *  these need a person to decide. */
export function DuplicatesPage() {
  const [status, setStatus] = useState<Status>("pending");
  const q = useInfiniteQuery({
    queryKey: ["match-reviews", status],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      api<{ reviews: MatchReview[]; pending: number; next_before_id?: number }>("/api/v1/match-reviews" + qs({ status, before_id: pageParam || undefined, limit: 10 })),
    getNextPageParam: (l) => l.next_before_id || undefined,
  });
  const reviews = q.data?.pages.flatMap((p) => p.reviews) ?? [];
  const pending = q.data?.pages[0]?.pending;
  return (
    <div className="mx-auto max-w-[1200px]">
      <PageHeader
        title="Possible duplicates"
        sub={pending !== undefined ? `${pending} waiting` : undefined}
        actions={
          <div role="radiogroup" aria-label="Show" className="flex gap-1">
            {(
              [
                ["pending", "Waiting"],
                ["merged", "Merged"],
                ["dismissed", "Not duplicates"],
              ] as const
            ).map(([v, label]) => (
              <Button key={v} size="sm" role="radio" aria-checked={status === v} variant={status === v ? "default" : "outline"} onClick={() => setStatus(v)}>
                {label}
              </Button>
            ))}
          </div>
        }
      >
        <p className="text-sm text-[var(--color-muted-foreground)]">
          People with the same email are merged automatically. These pairs matched on something weaker, so a person decides. Merging keeps the record you choose,
          moves the other one's emails, tags, orders and history into it, and saves a snapshot of both first.
        </p>
      </PageHeader>
      {q.isLoading && <Skeleton className="h-64" />}
      {!q.isLoading && reviews.length === 0 && (
        <Card>
          <Empty title={status === "pending" ? "Nothing waiting. Every possible duplicate has been decided." : "Nothing here yet."} />
        </Card>
      )}
      <div className="space-y-3">
        {reviews.map((r) => (
          <ReviewCard key={r.id} r={r} />
        ))}
      </div>
      {q.hasNextPage && (
        <Button className="mt-3" variant="outline" size="sm" onClick={() => q.fetchNextPage()} disabled={q.isFetchingNextPage}>
          Show more
        </Button>
      )}
    </div>
  );
}

function name(c: Contact | null): string {
  if (!c) return "(gone)";
  return [c.first_name, c.last_name].filter(Boolean).join(" ") || `#${c.id}`;
}

function rows(c: Contact | null): [string, React.ReactNode][] {
  if (!c) return [];
  const a = c.addresses.find((x) => x.kind === "billing") ?? c.addresses[0];
  return [
    ["Name", [c.prefix, c.first_name, c.middle_name, c.last_name, c.suffix].filter(Boolean).join(" ") + (c.nickname ? ` (${c.nickname})` : "")],
    ["Emails", c.emails.map((e) => e.email).join(", ") || "—"],
    ["Phones", c.phones.map((p) => p.number).join(", ") || "—"],
    ["Company", c.company || "—"],
    ["Address", a ? [a.line1, a.city, a.region, a.postal_code].filter(Boolean).join(", ") : "—"],
    [
      "Brands",
      c.brands.length ? (
        <span className="flex flex-wrap gap-2">
          {c.brands.map((b) => (
            <BrandMark key={b.brand_id} code={b.code} />
          ))}
        </span>
      ) : (
        "—"
      ),
    ],
    ["Tags", c.tags.length ? c.tags.map((t) => t.name).join(", ") : "—"],
    ["Lifetime value", `${formatCents(c.ltv_cents)} (${c.order_count} orders)`],
    ["Access", c.access_summary.join(", ") || "—"],
    ["Added", formatDate(c.created_at)],
  ];
}

function textOf(n: React.ReactNode): string {
  return typeof n === "string" ? n : "";
}

function ReviewCard({ r }: { r: MatchReview }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [winner, setWinner] = useState<Contact | null>(null);
  const decide = useMutation({
    mutationFn: (v: { decision: "merge" | "dismiss"; winner_id?: number }) => api(`/api/v1/match-reviews/${r.id}/decide`, { json: v }),
    onSuccess: (_d, v) => {
      toast(v.decision === "merge" ? `Merged into #${v.winner_id}` : "Marked as different people");
      void qc.invalidateQueries({ queryKey: ["match-reviews"] });
      void qc.invalidateQueries({ queryKey: ["match-reviews-count"] });
    },
    onError: (e) => toast(e.message, "error"),
  });
  const ra = rows(r.a);
  const rb = rows(r.b);
  const loser = winner && (winner.id === r.a?.id ? r.b : r.a);
  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2 text-sm">
        <Badge variant="default">{ruleLabel[r.rule] ?? r.rule}</Badge>
        {r.score != null && <span className="num text-xs text-[var(--color-muted-foreground)]">match {Math.round(r.score * 100)}%</span>}
        {typeof r.details?.reason === "string" && r.details.reason && <span className="text-xs text-[var(--color-muted-foreground)]">“{r.details.reason}”</span>}
        <span className="ml-auto text-xs text-[var(--color-muted-foreground)]">
          {r.status === "pending" ? `found ${formatDateTime(r.created_at)}` : `${r.status === "merged" ? "merged" : "dismissed"} ${formatDateTime(r.decided_at)} by ${r.decided_by}`}
        </span>
      </div>
      <div className="grid grid-cols-[8rem_minmax(0,1fr)_minmax(0,1fr)] text-sm">
        <div />
        {[r.a, r.b].map((c, i) => (
          <div key={i} className="border-l px-3 py-2">
            {c ? (
              <Link to="/contacts/$contactId" params={{ contactId: String(c.id) }} className="font-medium text-[var(--color-primary)] hover:underline">
                {name(c)} <span className="num text-xs text-[var(--color-muted-foreground)]">#{c.id}</span>
              </Link>
            ) : (
              "(gone)"
            )}
          </div>
        ))}
        {ra.map(([label, va], i) => {
          const vb = rb[i]?.[1];
          const differs = textOf(va) !== textOf(vb);
          return (
            <div key={label} className="contents">
              <div className="border-t px-3 py-1 text-xs text-[var(--color-muted-foreground)]">{label}</div>
              {[va, vb].map((v, j) => (
                <div key={j} className={cn("min-w-0 break-words border-l border-t px-3 py-1", differs && textOf(v) && "bg-[var(--color-row-selected)]")}>
                  {v ?? "—"}
                </div>
              ))}
            </div>
          );
        })}
      </div>
      {r.status === "pending" && r.a && r.b && (
        <div className="flex flex-wrap items-center gap-2 border-t px-4 py-2">
          <Button size="sm" variant="outline" onClick={() => setWinner(r.a)}>
            <ArrowLeft /> Keep #{r.a.id}, merge the other into it
          </Button>
          <Button size="sm" variant="outline" onClick={() => setWinner(r.b)}>
            Keep #{r.b.id}, merge the other into it <ArrowRight />
          </Button>
          <Button size="sm" variant="ghost" className="ml-auto" disabled={decide.isPending} onClick={() => decide.mutate({ decision: "dismiss" })}>
            Not the same person
          </Button>
        </div>
      )}
      <Dialog open={!!winner} onOpenChange={(o) => !o && setWinner(null)}>
        <DialogContent>
          <DialogTitle>
            Merge #{loser?.id} into #{winner?.id}?
          </DialogTitle>
          <DialogDescription>
            {name(winner)} (#{winner?.id}) stays. {name(loser)}'s emails, phones, tags, orders, notes and history move into it; where both have a value, #{winner?.id}'s
            wins. A note with a snapshot of both records is saved first, and #{loser?.id} keeps working as a link to the merged record.
          </DialogDescription>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setWinner(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={decide.isPending}
              onClick={() => winner && decide.mutate({ decision: "merge", winner_id: winner.id }, { onSettled: () => setWinner(null) })}
            >
              Merge
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
